import { t } from "../i18n"
import { Controller } from "@hotwired/stimulus"

// Cleans up Action Text content that contains plain HTML file attachments
// instead of proper Action Text blob attachments
export default class extends Controller {
  connect() {
    this.cleanupFileAttachments()
  }

  cleanupFileAttachments() {

    // Group file attachments into cards
    this.groupFileAttachments()
  }

  groupFileAttachments() {
    const trixContent = this.element
    const allDivs = Array.from(trixContent.querySelectorAll('.trix-content > div'))

    let fileGroups = []
    let currentGroup = null

    allDivs.forEach((div) => {
      const text = div.textContent.trim()

      // Start of a new file (Material Icon)
      if (text === 'insert_drive_file' || text === 'picture_as_pdf' || text === 'table_chart') {
        if (currentGroup) {
          fileGroups.push(currentGroup)
        }
        currentGroup = { icon: div, filename: null, size: null, links: [] }
        return
      }

      if (!currentGroup) return

      // Filename (has extension)
      if (text.match(/\.\w+$/)) {
        currentGroup.filename = div
        return
      }

      // File size
      if (text.match(/^\d+(\.\d+)?\s*(KB|MB|GB|bytes?)$/i)) {
        currentGroup.size = div
        return
      }

      // Container with links
      const links = div.querySelectorAll('a[href*="active_storage/blobs"]')
      if (links.length > 0) {
        currentGroup.links = Array.from(links)
        currentGroup.linkContainer = div
      }
    })

    // Don't forget the last group
    if (currentGroup) {
      fileGroups.push(currentGroup)
    }

    // Now create nice file cards
    fileGroups.forEach(group => {
      this.createFileCard(group)
    })
  }

  createFileCard(group) {
    // Create a card wrapper
    const card = document.createElement('div')
    card.className = 'not-prose my-4 bg-dark-bg rounded-lg border border-border-dark p-4 flex items-start gap-4 max-w-2xl'

    // Icon
    const iconWrapper = document.createElement('div')
    iconWrapper.className = 'flex-shrink-0 text-primary w-10 h-10 flex items-center justify-center'
    const icon = document.createElement('span')
    icon.className = 'material-symbols-outlined text-3xl'

    // Determine icon based on filename
    if (group.filename) {
      const filename = group.filename.textContent.toLowerCase()
      if (filename.endsWith('.pdf')) {
        icon.textContent = 'picture_as_pdf' // Material Symbols ligature (not translatable)
      } else if (filename.match(/\.(xlsx?|csv)$/)) {
        icon.textContent = 'table_chart'
      } else if (filename.match(/\.(docx?|txt)$/)) {
        icon.textContent = 'description'
      } else if (filename.match(/\.(jpg|jpeg|png|gif|webp)$/)) {
        icon.textContent = 'image'
      } else {
        icon.textContent = 'insert_drive_file'
      }
    }
    iconWrapper.appendChild(icon)
    card.appendChild(iconWrapper)

    // Content wrapper
    const content = document.createElement('div')
    content.className = 'flex-1 min-w-0'

    // Filename
    if (group.filename) {
      const filename = document.createElement('div')
      filename.className = 'font-medium text-[#E0E7FF] break-words mb-1'
      filename.textContent = group.filename.textContent
      content.appendChild(filename)
    }

    // File size
    if (group.size) {
      const size = document.createElement('div')
      size.className = 'text-sm text-[#A7B5D0] mb-3'
      size.textContent = group.size.textContent
      content.appendChild(size)
    }

    // Buttons container
    const buttonsContainer = document.createElement('div')
    buttonsContainer.className = 'flex gap-2 flex-wrap'

    // Get unique links (remove duplicates)
    const uniqueLinks = this.getUniqueLinks(group.links)

    uniqueLinks.forEach(link => {
      const btn = this.createStyledButton(link)
      if (btn) {
        buttonsContainer.appendChild(btn)
      }
    })

    content.appendChild(buttonsContainer)
    card.appendChild(content)

    // Insert the card before the icon and remove old elements
    if (group.icon && group.icon.parentNode) {
      group.icon.parentNode.insertBefore(card, group.icon)

      // Remove old elements
      group.icon.remove()
      if (group.filename) group.filename.remove()
      if (group.size) group.size.remove()
      if (group.linkContainer) group.linkContainer.remove()
    }
  }

  getUniqueLinks(links) {
    const seen = new Set()
    const unique = []

    links.forEach(link => {
      const key = link.href + '|' + (link.href.includes('disposition=attachment') ? 'download' : 'open')
      if (!seen.has(key)) {
        seen.add(key)
        unique.push(link)
      }
    })

    return unique
  }

  createStyledButton(link) {
    const isDownload = link.href.includes('disposition=attachment')
    const text = link.textContent.trim().toLowerCase()

    const btn = document.createElement('a')
    btn.href = link.href
    btn.target = isDownload ? '' : '_blank'
    btn.className = 'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors no-underline'

    if (isDownload || text === 'download') {
      btn.classList.add('bg-transparent', 'border', 'border-border-dark', 'text-[#A7B5D0]', 'hover:bg-slate-800')
      btn.innerHTML = `<span class="material-symbols-outlined text-base">download</span><span>${t("javascript.controllers.trix_content_controller.download")}</span>`
    } else {
      btn.classList.add('bg-slate-700', 'text-[#E0E7FF]', 'hover:bg-slate-600')
      btn.innerHTML = `<span class="material-symbols-outlined text-base">open_in_new</span><span>${t("javascript.controllers.trix_content_controller.open")}</span>`
    }

    return btn
  }
}

